// PNG export — the chart as a document (a referral, a printout).
//
// Serializes a CLONE of the live chart <svg>, injects a patient-context
// header, rasterizes it onto a canvas at 2× and downloads the PNG. The live
// DOM node is NEVER mutated — everything happens on the clone.
//
// Every DOM API on this path (canvas 2D, Image, Blob, URL, XMLSerializer) is
// guarded so the module is inert under jsdom/node: when any of them is
// missing, `exportChartPng` returns early with a clear error instead of
// half-downloading. No test renders the PNG itself — the guards are the
// testable surface.
//
// FONTS: the SVG is rasterized as a STANDALONE document, so the app's webfont
// is not guaranteed inside it. The Persian glyphs of the two permanent
// «راست بیمار» / «چپ بیمار» side markers may therefore fall back to the system
// font in the PNG. Accepted deliberately: embedding the font would add ~50 KB
// to every export for two small labels.

import { CHART_SHADES } from '../../lib/teeth.js';

const SVG_NS = 'http://www.w3.org/2000/svg';
const HEADER_H = 26;   // viewBox units added above the chart for the header
const SCALE = 2;       // rasterization factor — crisp enough to print

/** odontogram-<patientId>-<yyyymmdd>.png — pure, so the shape is testable.
 *  Gregorian on purpose: the FILENAME is a sortable ASCII artifact for
 *  machines; the Jalaali date a human reads sits in the header INSIDE the
 *  image, via the caller's locale-aware formatDate. */
export function exportFileName(patientId, date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `odontogram-${patientId}-${y}${m}${d}.png`;
}

/**
 * Export the chart svg as a downloaded PNG.
 *
 * @param svgEl     the LIVE chart <svg> (cloned here, never touched)
 * @param patientId used for the filename
 * @param header    the patient-context line drawn above the chart (an LTR
 *                  run: id + date)
 * @returns {ok:true, fileName} or {ok:false, error} — never throws.
 */
export async function exportChartPng({ svgEl, patientId, header }) {
  if (typeof document === 'undefined' || typeof Image === 'undefined'
      || typeof Blob === 'undefined' || typeof URL === 'undefined'
      || !URL.createObjectURL || typeof XMLSerializer === 'undefined') {
    return { ok: false, error: 'unsupported' };
  }
  if (!svgEl) return { ok: false, error: 'no-svg' };

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext && canvas.getContext('2d');
  // jsdom lands here: it has document/Image/Blob but no canvas backend.
  if (!ctx || !canvas.toBlob) return { ok: false, error: 'unsupported' };

  const box = (svgEl.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
  if (box.length !== 4 || box.some((n) => !Number.isFinite(n))) {
    return { ok: false, error: 'no-svg' };
  }
  const [minX, minY, w, h] = box;

  // Clone, then make the clone a self-contained document: explicit pixel
  // dimensions (the live node's width:100% / max-width CSS would clamp a
  // standalone rasterization), an explicit xmlns for the serializer, and the
  // same direction:ltr the live chart pins — the anatomical frame must hold
  // in the export exactly as on screen (CORE.md §9).
  const clone = svgEl.cloneNode(true);
  clone.removeAttribute('style');
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('direction', 'ltr');
  clone.setAttribute('font-family', 'sans-serif');   // see FONTS note above
  clone.setAttribute('viewBox', `${minX} ${minY - HEADER_H} ${w} ${h + HEADER_H}`);
  clone.setAttribute('width', String(w * SCALE));
  clone.setAttribute('height', String((h + HEADER_H) * SCALE));

  // The patient-context header, injected into the CLONE only. World
  // coordinates, unrotated (text stays upright), LTR — it is an id + a date.
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('x', String(minX + 10));
  text.setAttribute('y', String(minY - HEADER_H + 17));
  text.setAttribute('font-size', '12');
  text.setAttribute('font-weight', '700');
  text.setAttribute('fill', CHART_SHADES.exportHeader);
  text.setAttribute('direction', 'ltr');
  text.textContent = header || String(patientId ?? '');
  clone.insertBefore(text, clone.firstChild);

  const svgUrl = URL.createObjectURL(new Blob(
    [new XMLSerializer().serializeToString(clone)],
    { type: 'image/svg+xml;charset=utf-8' },
  ));

  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('svg-load'));
      i.src = svgUrl;
    });

    canvas.width = w * SCALE;
    canvas.height = (h + HEADER_H) * SCALE;
    ctx.fillStyle = CHART_SHADES.exportBackground;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const pngBlob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!pngBlob) return { ok: false, error: 'raster' };

    const fileName = exportFileName(patientId);
    const a = document.createElement('a');
    const pngUrl = URL.createObjectURL(pngBlob);
    a.href = pngUrl;
    a.download = fileName;
    a.rel = 'noopener';
    a.click();
    // Revoke on the next macrotask turn — the click's navigation has started
    // by then, and holding the URL longer only leaks the blob.
    setTimeout(() => URL.revokeObjectURL(pngUrl), 0);
    return { ok: true, fileName };
  } catch {
    return { ok: false, error: 'raster' };
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
}
